import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'src/store';
import { resetFilteredTypes } from 'src/store/slices/knowledgeBaseSlices';
import { getKnowledgeBaseConfig, setKnowledgeBaseConfig } from 'src/utils/pageStorageConfig';
import { filterBranchByName, findActiveBranch, getFoldersStructure } from 'src/utils/fileUtils';
import type { Integration, KBFile, TreeNode } from 'src/utils/fileUtils';

const LAZY_COLLECTOR_TYPES = new Set(['web_scrapper', 'zendesk_helpcenter']);

interface UseKnowledgeBaseTreeParams {
  knowledgeBaseId: string | undefined;
  tree: TreeNode;
  setTree: (tree: TreeNode) => void;
  activeBranch: TreeNode;
  updateActiveBranch: (branch: TreeNode) => void;
  setFilteredValue: (value: string) => void;
  setKnowledgeBaseName: (name: string) => void;
  files: KBFile[];
  integrations: Integration[];
  currentKnowledgeBase?: { name: string };
  skipRefetchFiles: boolean;
  isSuccessFiles: boolean;
  isV1: boolean;
  getLazyKnowledgeBaseFiles: (args: { id?: string; collector_id: string; sync: boolean }) => void;
}

export const useKnowledgeBaseTree = ({
  knowledgeBaseId,
  tree,
  setTree,
  activeBranch,
  updateActiveBranch,
  setFilteredValue,
  setKnowledgeBaseName,
  files,
  integrations,
  currentKnowledgeBase,
  skipRefetchFiles,
  isSuccessFiles,
  isV1,
  getLazyKnowledgeBaseFiles,
}: UseKnowledgeBaseTreeParams) => {
  const dispatch = useAppDispatch();
  const { filteredTypes } = useAppSelector((s) => s.knowledgeBaseSlices);

  useEffect(() => {
    if (filteredTypes) {
      updateActiveBranch({ ...activeBranch });
    }
  }, [filteredTypes]);

  useEffect(() => {
    setKnowledgeBaseConfig({ knowledgeBaseId });

    return () => {
      dispatch(resetFilteredTypes());
    };
  }, []);

  useEffect(() => {
    if (currentKnowledgeBase) {
      setKnowledgeBaseName(currentKnowledgeBase.name);
      setTree({ ...tree, name: currentKnowledgeBase.name });
    }
  }, [currentKnowledgeBase]);

  useEffect(() => {
    if (skipRefetchFiles) {
      return;
    }

    const foldersStructure = getFoldersStructure(files, integrations);
    const branch = findActiveBranch(foldersStructure);

    setTree(foldersStructure);
    updateActiveBranch(branch || foldersStructure);
  }, [files, integrations, skipRefetchFiles]);

  useEffect(() => {
    if (!activeBranch.isTemporary && isSuccessFiles) {
      const config = getKnowledgeBaseConfig();

      config.activeBranchPath = activeBranch.path;
      setKnowledgeBaseConfig(config);
    }

    if (!isV1 && LAZY_COLLECTOR_TYPES.has(activeBranch?.data?.type)) {
      getLazyKnowledgeBaseFiles({
        id: knowledgeBaseId,
        collector_id: activeBranch.data.id,
        sync: false,
      });
    }
  }, [activeBranch]);

  const onFileSearch = (value: string) => {
    if (value) {
      const newActiveBranch = filterBranchByName(value, tree);
      newActiveBranch.isTemporary = true;

      updateActiveBranch(newActiveBranch);
    } else if (files.length) {
      const foldersStructure = getFoldersStructure(files, integrations);
      const branch = findActiveBranch(foldersStructure);

      setTree(foldersStructure);
      branch && updateActiveBranch(branch || foldersStructure);
    }

    setFilteredValue(value);
  };

  return { onFileSearch };
};
